/**
 * referral-queue-filters.tsx — Status + search filters for the referral queue.
 *
 * Props:
 *   items          — full (unfiltered) list from server
 *   onFilter       — called with filtered subset whenever search input changes
 *   onStatusChange — called with new status when the status dropdown changes
 *
 * Filtering logic (client-side):
 *   - 10-digit input → NPI exact match
 *   - Otherwise → case-insensitive partial match on patient firstName + lastName
 */

import { useState, useEffect, type JSX } from 'react';

import type { PendingReferralSummary, ReferralQueueStatus } from './referral-api.js';

export interface ReferralQueueFiltersProps {
  items: PendingReferralSummary[];
  onFilter: (filtered: PendingReferralSummary[]) => void;
  onStatusChange: (status: ReferralQueueStatus) => void;
}

const NPI_PATTERN = /^\d{10}$/;

function applyFilter(items: PendingReferralSummary[], search: string): PendingReferralSummary[] {
  const q = search.trim();
  if (!q) return items;

  if (NPI_PATTERN.test(q)) {
    return items.filter((r) => r.providerNpi === q);
  }

  const lower = q.toLowerCase();
  return items.filter((r) => {
    const full = `${r.patientName.firstName} ${r.patientName.lastName}`.toLowerCase();
    return full.includes(lower);
  });
}

export function ReferralQueueFilters({
  items,
  onFilter,
  onStatusChange,
}: ReferralQueueFiltersProps): JSX.Element {
  const [status, setStatus] = useState<ReferralQueueStatus>('awaiting_provider_review');
  const [search, setSearch] = useState('');

  // Whenever items or search changes, re-apply the filter
  useEffect(() => {
    onFilter(applyFilter(items, search));
  }, [items, search, onFilter]);

  function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const newStatus = e.target.value as ReferralQueueStatus;
    setStatus(newStatus);
    onStatusChange(newStatus);
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(e.target.value);
  }

  function handleClear(): void {
    setSearch('');
    setStatus('awaiting_provider_review');
    onStatusChange('awaiting_provider_review');
    onFilter(items);
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor="queue-status" className="text-xs font-medium text-gray-600">
          Status
        </label>
        <select
          id="queue-status"
          value={status}
          onChange={handleStatusChange}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="awaiting_provider_review">Awaiting Review</option>
          <option value="rejected">Rejected</option>
          <option value="ineligible">Ineligible</option>
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="queue-search" className="text-xs font-medium text-gray-600">
          Search
        </label>
        <input
          id="queue-search"
          type="text"
          placeholder="Search by patient name or NPI"
          value={search}
          onChange={handleSearchChange}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <button
        type="button"
        onClick={handleClear}
        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
      >
        Clear
      </button>
    </div>
  );
}
